/* eslint-disable max-len */
// The value prop descriptions are single sentences that run past the 120
// column limit; breaking them would break `defaultMessage` extraction.

/*
 * Copyright (c) 2026, WSO2 LLC. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 LLC. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you've
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import { Box, SvgIcon, Typography } from '@material-ui/core';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  Network,
  Rocket,
  Sparkles,
  SquareTerminal,
} from 'components/ChoreoSystem/Icons/generated';
import useStyles from './LandingValueProps.styles';

/**
 * The platform lifecycle diagram. The same artwork the old login page carried
 * on its left-hand panel, which is why it lives in `public` rather than being
 * imported — it is served, not bundled.
 */
const LIFECYCLE_ARTWORK_SRC = '/images/login.svg';

interface ValueProp {
  key: string;
  Icon: typeof SvgIcon;
  title: React.ReactNode;
  description: React.ReactNode;
}

/**
 * Built once at module scope: these are constant, and `FormattedMessage`
 * re-resolves against the active locale on render either way.
 */
const VALUE_PROPS: ValueProp[] = [
  {
    key: 'integratedDevelopmentExperience',
    Icon: SquareTerminal,
    title: (
      <FormattedMessage
        id="modules.login.landingValueProps.integratedDevelopmentExperience.title"
        defaultMessage="Integrated Development Experience"
      />
    ),
    description: (
      <FormattedMessage
        id="modules.login.landingValueProps.integratedDevelopmentExperience.description"
        defaultMessage="Build and manage integrations, long-running workflows, MCP servers, and AI agents on a single platform."
      />
    ),
  },
  {
    key: 'aiGovernance',
    Icon: Sparkles,
    title: (
      <FormattedMessage
        id="modules.login.landingValueProps.aiGovernance.title"
        defaultMessage="AI Governance"
      />
    ),
    description: (
      <FormattedMessage
        id="modules.login.landingValueProps.aiGovernance.description"
        defaultMessage="Build dependable AI solutions with enterprise context, evaluations, human-in-the-loop workflows, governance, and observability."
      />
    ),
  },
  {
    key: 'enterpriseConnectivity',
    Icon: Network,
    title: (
      <FormattedMessage
        id="modules.login.landingValueProps.enterpriseConnectivity.title"
        defaultMessage="Enterprise Connectivity"
      />
    ),
    description: (
      <FormattedMessage
        id="modules.login.landingValueProps.enterpriseConnectivity.description"
        defaultMessage="Connect applications, APIs, events, files, and data with pre-built connectors and native protocol support."
      />
    ),
  },
  {
    key: 'flexibleDelivery',
    Icon: Rocket,
    title: (
      <FormattedMessage
        id="modules.login.landingValueProps.flexibleDelivery.title"
        defaultMessage="Flexible Delivery"
      />
    ),
    description: (
      <FormattedMessage
        id="modules.login.landingValueProps.flexibleDelivery.description"
        defaultMessage="Align environments with your SDLC and automate promotion from development to production."
      />
    ),
  },
];

/**
 * What the platform does, below the prompt on the login landing page.
 *
 * The four points, their icons and their copy come from the design; they sit
 * next to the lifecycle diagram the old login page carried on its left-hand
 * panel.
 */
export default function LandingValueProps() {
  const classes = useStyles();
  const intl = useIntl();

  return (
    <Box className={classes.valuePropsRoot} data-testid="landing-value-props">
      <Box className={classes.artworkWrap}>
        <img
          className={classes.artwork}
          src={LIFECYCLE_ARTWORK_SRC}
          alt={intl.formatMessage({
            id: 'modules.login.landingValueProps.artworkAlt',
            defaultMessage:
              'The platform lifecycle: develop, integrate and test, then promote, manage and observe.',
          })}
        />
      </Box>

      <ul className={classes.propsList}>
        {VALUE_PROPS.map(({ key, Icon, title, description }) => (
          <li key={key} className={classes.propItem}>
            <Icon className={classes.propIcon} />
            <Box>
              <Typography variant="h4" className={classes.propTitle}>
                {title}
              </Typography>
              <Typography variant="body1" color="textSecondary">
                {description}
              </Typography>
            </Box>
          </li>
        ))}
      </ul>
    </Box>
  );
}
